/**
 * Family — admin view of every non-resident family member attached to a unit.
 *
 * The resident-side self-service flow lives on the mobile app
 * (/v1/residents/me/family). This page wraps the admin endpoints
 * (/v1/family) so society staff can add or correct family on behalf of
 * residents — common when onboarding a new flat or helping elderly
 * residents who don't use the app.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit3, Trash2, Users2 } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate } from '@/lib/format';

interface FamilyRow {
  id: string;
  society_id: string;
  resident_id: string;
  full_name: string;
  relation: string;
  date_of_birth: string | null;
  phone: string | null;
  created_at: string;
  resident: {
    id: string;
    full_name: string;
    unit_id: string;
    unit: { number: string; block: { name: string | null } | null } | null;
  };
}

interface ListResponse {
  items: FamilyRow[];
  next_cursor: string | null;
}

interface UnitLite {
  id: string;
  number: string;
  block_id: string | null;
}
interface BlockLite {
  id: string;
  name: string;
}

interface ResidentLite {
  id: string;
  full_name: string;
  unit_id: string;
}

const RELATION_OPTIONS = ['spouse', 'parent', 'child', 'sibling', 'in_law', 'other'] as const;

const RELATION_LABEL: Record<string, string> = {
  spouse: 'Spouse',
  parent: 'Parent',
  child: 'Child',
  sibling: 'Sibling',
  in_law: 'In-law',
  other: 'Other'
};

const CreateSchema = z.object({
  resident_id: z.string().uuid('Pick a resident'),
  full_name: z.string().trim().min(2, 'Name required').max(120),
  relation: z.string().min(2, 'Pick a relation').max(40),
  date_of_birth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD').optional().or(z.literal('')),
  phone: z.string().regex(/^\+?[1-9]\d{6,14}$/, 'E.164 format').optional().or(z.literal(''))
});
type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULT_FORM: CreateForm = {
  resident_id: '',
  full_name: '',
  relation: 'spouse',
  date_of_birth: '',
  phone: ''
};

const FILTER_DEFAULTS = {
  search: '',
  unit_id: '',
  resident_id: '',
  relation: 'all',
  sort: 'created_desc',
  page_size: 50
};

export function FamilyPage(): JSX.Element {
  const qc = useQueryClient();
  const [filterState, setFilterState] = useUrlSearchState(FILTER_DEFAULTS);
  const [debounced, setDebounced] = useState(filterState.search);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(filterState.search), 300);
    return () => clearTimeout(t);
  }, [filterState.search]);

  const unitsQuery = useQuery({
    queryKey: ['units', 'lite'],
    queryFn: () => get<{ items: UnitLite[] }>('/society/units', { params: { limit: 500 } })
  });
  const blocksQuery = useQuery({
    queryKey: ['blocks', 'lite'],
    queryFn: () => get<{ items: BlockLite[] }>('/society/blocks')
  });
  const residentsQuery = useQuery({
    queryKey: ['residents', 'lite'],
    queryFn: () => get<{ items: ResidentLite[] }>('/residents', { params: { limit: 500 } })
  });

  const unitLabel = (u: UnitLite): string => {
    const block = blocksQuery.data?.items.find((b) => b.id === u.block_id);
    const prefix = block?.name ? `${block.name}-` : '';
    return prefix && u.number.startsWith(prefix) ? u.number : `${prefix}${u.number}`;
  };

  const familyFiltersHash = useMemo(
    () =>
      JSON.stringify([
        debounced,
        filterState.unit_id,
        filterState.resident_id,
        filterState.relation,
        filterState.sort,
        filterState.page_size
      ]),
    [debounced, filterState]
  );
  const [familyCursor, setFamilyCursor] = useState<string | null>(null);
  useEffect(() => {
    setFamilyCursor(null);
  }, [familyFiltersHash]);
  const listQuery = useQuery({
    queryKey: [
      'family',
      'admin',
      debounced,
      filterState.unit_id,
      filterState.resident_id,
      filterState.relation,
      filterState.sort,
      filterState.page_size,
      familyCursor
    ],
    queryFn: () =>
      get<ListResponse>('/family', {
        params: {
          ...(debounced ? { q: debounced } : {}),
          ...(filterState.unit_id ? { unit_id: filterState.unit_id } : {}),
          ...(filterState.resident_id ? { resident_id: filterState.resident_id } : {}),
          sort: filterState.sort,
          limit: filterState.page_size,
          ...(familyCursor ? { cursor: familyCursor } : {})
        }
      })
  });
  const familyPaginator = useCursorAccumulator<FamilyRow>(listQuery.data, familyCursor, familyFiltersHash);

  const items = familyPaginator.items;
  // Client-side relation filter (backend doesn't have one yet — keeping the
  // BE schema minimal; if the catalog grows we'll lift this into a real query).
  const filtered =
    filterState.relation === 'all'
      ? items
      : items.filter((f) => f.relation.toLowerCase() === filterState.relation.toLowerCase());

  const filterChips: FilterChip[] = [
    {
      id: 'unit_id',
      label: 'Unit',
      type: 'select',
      value: filterState.unit_id,
      options: [
        { value: '', label: 'All units' },
        ...(unitsQuery.data?.items ?? []).map((u) => ({ value: u.id, label: unitLabel(u) }))
      ],
      onChange: (v) => setFilterState({ unit_id: v })
    },
    {
      id: 'resident_id',
      label: 'Resident',
      type: 'select',
      value: filterState.resident_id,
      options: [
        { value: '', label: 'All residents' },
        ...(residentsQuery.data?.items ?? []).map((r) => ({ value: r.id, label: r.full_name }))
      ],
      onChange: (v) => setFilterState({ resident_id: v })
    },
    {
      id: 'relation',
      label: 'Relation',
      type: 'select',
      value: filterState.relation,
      options: [
        { value: 'all', label: 'All relations' },
        ...RELATION_OPTIONS.map((r) => ({ value: r, label: RELATION_LABEL[r]! }))
      ],
      onChange: (v) => setFilterState({ relation: v })
    }
  ];

  const sortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Recently added' },
      { value: 'created_asc', label: 'Oldest first' },
      { value: 'name_asc', label: 'Name (A→Z)' },
      { value: 'name_desc', label: 'Name (Z→A)' }
    ],
    []
  );

  const hasActiveFilters =
    filterState.search !== '' ||
    filterState.unit_id !== '' ||
    filterState.resident_id !== '' ||
    filterState.relation !== 'all';
  const resetFilters = (): void => setFilterState(FILTER_DEFAULTS);

  // ───── add / edit dialog ─────
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<FamilyRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FamilyRow | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULT_FORM,
    mode: 'onChange'
  });

  const openCreate = (presetResidentId?: string): void => {
    setEditing(null);
    form.reset({ ...DEFAULT_FORM, ...(presetResidentId ? { resident_id: presetResidentId } : {}) });
    setOpen(true);
  };
  const openEdit = (f: FamilyRow): void => {
    setEditing(f);
    form.reset({
      resident_id: f.resident_id,
      full_name: f.full_name,
      relation: f.relation,
      date_of_birth: f.date_of_birth ? f.date_of_birth.slice(0, 10) : '',
      phone: f.phone ?? ''
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: (v: CreateForm) => {
      const body = {
        full_name: v.full_name,
        relation: v.relation,
        date_of_birth: v.date_of_birth || undefined,
        phone: v.phone?.trim() || undefined,
        ...(editing ? {} : { resident_id: v.resident_id })
      };
      if (editing) return patch<FamilyRow>(`/family/${editing.id}`, body);
      return post<FamilyRow>('/family', body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Family member updated' : 'Family member added');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['family', 'admin'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del<void>(`/family/${id}`),
    onSuccess: () => {
      toast.success('Family member removed');
      setConfirmDelete(null);
      void qc.invalidateQueries({ queryKey: ['family', 'admin'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setConfirmDelete(null);
    }
  });

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Family members"
        description="Non-resident family attached to a unit (spouse, parents, children, …). Resident self-service still works on the mobile app — this page lets society staff help with bulk onboarding."
        actions={
          <Button onClick={() => openCreate()}>
            <Plus className="h-4 w-4" /> Add family member
          </Button>
        }
      />

      <AdvancedListToolbar
        searchValue={filterState.search}
        onSearchChange={(v) => setFilterState({ search: v })}
        searchPlaceholder="Name or phone"
        filters={filterChips}
        sort={{
          value: filterState.sort,
          options: sortOptions,
          onChange: (v) => setFilterState({ sort: v })
        }}
        pageSize={filterState.page_size}
        onPageSizeChange={(n) => setFilterState({ page_size: n })}
        hasActiveFilters={hasActiveFilters}
        onReset={resetFilters}
      />

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-vt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Users2 className="h-6 w-6" />}
              title={hasActiveFilters ? 'No family members match these filters' : 'No family members yet'}
              description={
                hasActiveFilters
                  ? 'Try clearing filters or change the search.'
                  : 'Add family members linked to existing residents.'
              }
              action={
                hasActiveFilters ? (
                  <Button variant="ghost" onClick={resetFilters}>
                    Reset filters
                  </Button>
                ) : (
                  <Button onClick={() => openCreate()}>
                    <Plus className="h-4 w-4" /> Add family member
                  </Button>
                )
              }
            />
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Relation</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>DOB</TableHead>
                  <TableHead>Linked resident</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((f) => {
                  const blockName = f.resident?.unit?.block?.name;
                  const unitNumber = f.resident?.unit?.number ?? '—';
                  const prefix = blockName ? `${blockName}-` : '';
                  const unitLabelStr =
                    prefix && unitNumber.startsWith(prefix) ? unitNumber : `${prefix}${unitNumber}`;
                  return (
                    <TableRow key={f.id}>
                      <TableCell className="font-medium text-vt-text">{f.full_name}</TableCell>
                      <TableCell>
                        <Badge variant="muted">
                          {RELATION_LABEL[f.relation.toLowerCase()] ?? f.relation}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-vt-text-muted">{f.phone ?? '—'}</TableCell>
                      <TableCell className="text-vt-text-muted text-vt-12">
                        {f.date_of_birth ? formatDate(f.date_of_birth) : '—'}
                      </TableCell>
                      <TableCell className="text-vt-text-muted">
                        {f.resident?.full_name ?? '—'}
                      </TableCell>
                      <TableCell className="font-mono text-vt-12 text-vt-text-muted">
                        {unitLabelStr}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(f)}
                            title="Edit"
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setConfirmDelete(f)}
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <CursorPaginator
              hasMore={Boolean(familyPaginator.nextCursor)}
              loading={listQuery.isFetching}
              itemsShown={items.length}
              pageSize={filterState.page_size}
              onLoadMore={() => {
                if (familyPaginator.nextCursor) setFamilyCursor(familyPaginator.nextCursor);
              }}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : setOpen(false))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit family member' : 'Add family member'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Update name, relation, DOB or phone.'
                : 'Pick the linked resident first, then enter family details.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="fam-resident">Linked resident</Label>
              <Select
                id="fam-resident"
                {...form.register('resident_id')}
                disabled={Boolean(editing)}
              >
                <option value="">— Pick a resident —</option>
                {(residentsQuery.data?.items ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name}
                  </option>
                ))}
              </Select>
              {form.formState.errors.resident_id ? (
                <p className="text-vt-12 text-vt-alert">
                  {form.formState.errors.resident_id.message}
                </p>
              ) : null}
              {editing ? (
                <p className="text-vt-11 text-vt-text-muted">
                  Linked resident is immutable. Delete + re-add to move family to another resident.
                </p>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fam-name">Full name</Label>
                <Input id="fam-name" placeholder="Sita Devi" {...form.register('full_name')} />
                {form.formState.errors.full_name ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.full_name.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fam-relation">Relation</Label>
                <Select id="fam-relation" {...form.register('relation')}>
                  {RELATION_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {RELATION_LABEL[r]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fam-dob">Date of birth (optional)</Label>
                <Input id="fam-dob" type="date" {...form.register('date_of_birth')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="fam-phone">Phone (optional)</Label>
                <Input id="fam-phone" placeholder="+91…" {...form.register('phone')} />
                {form.formState.errors.phone ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.phone.message}</p>
                ) : null}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingText="Saving…"
                disabled={save.isPending}
              >
                {editing ? 'Save changes' : 'Add family member'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(confirmDelete)}
        onOpenChange={(v) => (v ? null : setConfirmDelete(null))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove family member?</DialogTitle>
            <DialogDescription>
              {confirmDelete ? (
                <>
                  Remove <span className="font-semibold">{confirmDelete.full_name}</span> ({confirmDelete.relation})?
                  This action is permanent — the resident can re-add via mobile if needed.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              loadingText="Removing…"
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
